import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Form, Table, Modal } from 'react-bootstrap';
import { State } from '../../../state';
import companyUserService from '../../../services/companyUserService';
import { setGlobalAlert } from '../../../state/action-creators/alert';

interface Role {
  role: string;
  id: number;
}

interface CompanyUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  mobilePhone: string | null;
  roles: Role[];
  username: string;
  password?: string;
  preferredLanguage: string;
  enabled: boolean;
}

interface EditUserData {
  id: number;
  username: string;
  password?: string;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
  preferredLanguage: string;
  enabled: boolean;
  roles: number[];
}

export default function CompanyUserManagement() {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: '',
    preferredLanguage: 'tr',
    enabled: true,
    roles: [] as number[],
  });
  const [languageData, setLanguageData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<EditUserData | null>(null);

  const state = useSelector((state: State) => state);
  const stateLanguage = state.languageReducer;
  const user = state.authReducer;

  useEffect(() => {
    console.log("CompanyUserManagement mounted");
    console.log("User roles:", state.authReducer.user?.roles);
    setLanguageData(stateLanguage);
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const response = await companyUserService.getUsers();
      console.log("Users loaded:", response.data);
      setUsers(response.data);
    } catch (err) {
      console.error("Failed to load users:", err);
      dispatch(setGlobalAlert('Failed to load users', 'error'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await companyUserService.getRoles();
      console.log("Roles loaded:", response.data);
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to load roles:", err);
      dispatch(setGlobalAlert('Failed to load roles', 'error'));
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (!newUser.username.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required, 'error'));
      isValid = false;
    }

    if (!newUser.password.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required, 'error'));
      isValid = false;
    }

    if (!newUser.firstName.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'First name is required', 'error'));
      isValid = false;
    }

    if (!newUser.lastName.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Last name is required', 'error'));
      isValid = false;
    }

    if (!newUser.email.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Email is required', 'error'));
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.invalidEmail || 'Invalid email format', 'error'));
      isValid = false;
    }

    if (!newUser.mobilePhone.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Phone number is required', 'error'));
      isValid = false;
    } else {
      const cleanPhone = newUser.mobilePhone.replace(/\D/g, '');
      const phoneRegex = /^(0?5[0-9]{9})$/;
      
      if (!phoneRegex.test(cleanPhone)) {
        dispatch(setGlobalAlert(languageData?.data?.validation?.invalidPhone || 'Please enter a valid phone number (05XX XXX XX XX)', 'error'));
        isValid = false;
      }
    }

    if (newUser.roles.length === 0) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.roleRequired || 'At least one role is required', 'error'));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const userData = {
        username: newUser.username.trim(),
        password: newUser.password.trim(),
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        email: newUser.email.trim(),
        mobilePhone: newUser.mobilePhone.trim(),
        preferredLanguage: newUser.preferredLanguage,
        enabled: newUser.enabled,
        roles: newUser.roles
      };

      await companyUserService.createUser(userData);
      await loadUsers();
      setNewUser({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        preferredLanguage: 'en',
        enabled: true,
        roles: [],
      });
      dispatch(setGlobalAlert(languageData?.data?.companyUser?.alerts?.createSuccess, 'success'));
    } catch (err: any) {
      console.error("Failed to create user:", err);
      dispatch(
        setGlobalAlert(
          err.response?.data?.message || 'Failed to create user',
          'error',
          err.response?.data?.details
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (roleId: number) => {
    setNewUser(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }));
  };

  const handleEditClick = (user: CompanyUser) => {
    setEditingUser({
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email || '',
      mobilePhone: user.mobilePhone || '',
      preferredLanguage: user.preferredLanguage || 'en',
      enabled: user.enabled || true,
      roles: user.roles.map(r => r.id)
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    if (!validateEditForm()) return;

    setIsLoading(true);
    try {
      const userData = {
        username: editingUser.username.trim(),
        ...(editingUser.password && { password: editingUser.password }),
        firstName: editingUser.firstName.trim(),
        lastName: editingUser.lastName.trim(),
        email: editingUser.email.trim(),
        mobilePhone: editingUser.mobilePhone.trim(),
        preferredLanguage: editingUser.preferredLanguage,
        enabled: editingUser.enabled
      };

      await companyUserService.updateUser(editingUser.id, userData);
      await loadUsers();
      setShowEditModal(false);
      setEditingUser(null);
      dispatch(setGlobalAlert(languageData?.data?.companyUser?.alerts?.updateSuccess, 'success'));
    } catch (err: any) {
      console.error("Failed to update user:", err);
      dispatch(setGlobalAlert(err.response?.data?.message || 'Failed to update user', 'error'));
    } finally {
      setIsLoading(false);
    }
  };

  const validateEditForm = () => {
    let isValid = true;

    if (!editingUser) return false;

    if (!editingUser.firstName.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'First name is required', 'error'));
      isValid = false;
    }

    if (!editingUser.lastName.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Last name is required', 'error'));
      isValid = false;
    }

    if (!editingUser.email.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Email is required', 'error'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editingUser.email)) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.invalidEmail || 'Invalid email format', 'error'));
      isValid = false;
    }

    if (!editingUser.mobilePhone.trim()) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.required || 'Phone number is required', 'error'));
    } else {
      const cleanPhone = editingUser.mobilePhone.replace(/\D/g, '');
      const phoneRegex = /^(0?5[0-9]{9})$/;
      
      if (!phoneRegex.test(cleanPhone)) {
        dispatch(setGlobalAlert(languageData?.data?.validation?.invalidPhone || 'Please enter a valid phone number (05XX XXX XX XX)', 'error'));
        isValid = false;
      }
    }

    if (editingUser.roles.length === 0) {
      dispatch(setGlobalAlert(languageData?.data?.validation?.roleRequired || 'At least one role is required', 'error'));
      isValid = false;
    }

    return isValid;
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">{languageData?.data?.companyUser?.title}</h2>

      {/* New User Form */}
      <div className="card mb-4">
        <div className="card-header">
          <h3>{languageData?.data?.companyUser?.addNewUser}</h3>
        </div>
        <div className="card-body">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.username} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.password} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.firstName}</Form.Label>
              <Form.Control
                type="text"
                value={newUser.firstName}
                onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.lastName}</Form.Label>
              <Form.Control
                type="text"
                value={newUser.lastName}
                onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.email}</Form.Label>
              <Form.Control
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.mobilePhone}</Form.Label>
              <Form.Control
                type="tel"
                value={newUser.mobilePhone}
                onChange={(e) => setNewUser(prev => ({ ...prev, mobilePhone: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.editForm?.language?.title} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={newUser.preferredLanguage}
                onChange={(e) => setNewUser(prev => ({ ...prev, preferredLanguage: e.target.value }))}
              >
                <option value="en">{languageData?.data?.companyUser?.editForm?.language?.en}</option>
                <option value="tr">{languageData?.data?.companyUser?.editForm?.language?.tr}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="user-enabled-create"
                label={languageData?.data?.companyUser?.editForm?.userStatus}
                checked={newUser.enabled}
                onChange={(e) => setNewUser(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.roles}</Form.Label>
              <div>
                {availableRoles.map(role => (
                  <Form.Check
                    key={role.id}
                    type="checkbox"
                    label={role.role}
                    checked={newUser.roles.includes(role.id)}
                    onChange={() => handleRoleChange(role.id)}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>

            <Button 
              type="submit" 
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              {isLoading && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              {languageData?.data?.companyUser?.buttons.save}
            </Button>
          </Form>
        </div>
      </div>

      {/* Users List */}
      <div className="card">
        <div className="card-header">
          <h3>{languageData?.data?.companyUser?.usersList}</h3>
        </div>
        <div className="card-body">
          <Table responsive>
            <thead>
              <tr>
                <th>{languageData?.data?.companyUser?.firstName}</th>
                <th>{languageData?.data?.companyUser?.lastName}</th>
                <th>{languageData?.data?.companyUser?.email}</th>
                <th>{languageData?.data?.companyUser?.mobilePhone}</th>
                <th>{languageData?.data?.companyUser?.roles}</th>
                <th>{languageData?.data?.companyUser?.actions}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={index}>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{user.email || '-'}</td>
                  <td>{user.mobilePhone || '-'}</td>
                  <td>
                    {user.roles.map(role => role.role).join(', ')}
                  </td>
                  <td>
                    <Button 
                      variant="warning" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleEditClick(user)}
                    >
                      {languageData?.data?.companyUser?.buttons.edit}
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm"
                    >
                      {languageData?.data?.companyUser?.buttons.delete}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{languageData?.data?.companyUser?.editForm?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.username} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editingUser?.username || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, username: e.target.value } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.password}</Form.Label>
              <Form.Control
                type="password"
                value={editingUser?.password || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, password: e.target.value } : null)}
                placeholder={languageData?.data?.companyUser?.editForm?.passwordPlaceholder}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.firstName} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editingUser?.firstName || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, firstName: e.target.value } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.lastName} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editingUser?.lastName || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, lastName: e.target.value } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.email} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={editingUser?.email || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.mobilePhone} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                value={editingUser?.mobilePhone || ''}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, mobilePhone: e.target.value } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {languageData?.data?.companyUser?.editForm?.language?.title} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={editingUser?.preferredLanguage || 'en'}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, preferredLanguage: e.target.value } : null)}
              >
                <option value="en">{languageData?.data?.companyUser?.editForm?.language?.en}</option>
                <option value="tr">{languageData?.data?.companyUser?.editForm?.language?.tr}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="user-enabled"
                label={languageData?.data?.companyUser?.editForm?.userStatus}
                checked={editingUser?.enabled}
                onChange={(e) => setEditingUser(prev => prev ? { ...prev, enabled: e.target.checked } : null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{languageData?.data?.companyUser?.roles}</Form.Label>
              <div>
                {availableRoles.map(role => (
                  <Form.Check
                    key={role.id}
                    type="checkbox"
                    label={role.role}
                    checked={editingUser?.roles.includes(role.id)}
                    onChange={() => {
                      if (!editingUser) return;
                      setEditingUser(prev => {
                        if (!prev) return null;
                        return {
                          ...prev,
                          roles: prev.roles.includes(role.id)
                            ? prev.roles.filter(r => r !== role.id)
                            : [...prev.roles, role.id]
                        };
                      });
                    }}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                {languageData?.data?.general?.modal?.cancel}
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="d-flex align-items-center"
              >
                {isLoading && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )}
                {languageData?.data?.companyUser?.buttons.save}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
} 